/**
 * Digital Product utilities for web platform
 * Contains helper functions for digital music sales and downloads
 */

/**
 * Checks if the website supports digital products
 * @param {Object} website - Website configuration object
 * @returns {boolean} Whether digital products are supported
 */
export const supportsDigitalProducts = (website) => {
  if (!website) return false;

  // Check if the website has digital product features enabled
  return (
    website.features?.digitalProducts === true ||
    website.features?.shop?.digital === true ||
    website.features?.shop === true
  );
};

/**
 * Checks if a product is a digital product
 * @param {Object} product - Product object
 * @returns {boolean} Whether the product is digital
 */
export const isDigitalProduct = (product) => {
  if (!product) return false;
  return product.type === "digital" || product.is_digital === true;
};

/**
 * Gets the download format options for a digital product
 * @param {Object} product - Digital product object
 * @returns {Array} Array of available download formats
 */
export const getAvailableFormats = (product) => {
  if (!isDigitalProduct(product)) return [];

  const defaultFormats = ["mp3", "flac"];
  return product.available_formats || defaultFormats;
};

/**
 * Gets available product types for a website
 * @param {boolean} supportsDigital - Whether the website supports digital products
 * @returns {Array} Array of available product type objects with value, label, icon, and description
 */
export const getAvailableProductTypes = (supportsDigital) => {
  const productTypes = [
    {
      value: "physical",
      label: "Physical Product",
      icon: "📦",
      description: "Tangible items that require shipping",
    },
  ];

  if (supportsDigital) {
    productTypes.push(
      {
        value: "digital",
        label: "Digital Product",
        icon: "🎵",
        description: "Downloadable files (music, documents, etc.)",
      },
      {
        value: "bundle",
        label: "Bundle",
        icon: "📦🎵",
        description: "Combination of physical and digital items",
      },
    );
  }

  return productTypes;
};

/**
 * Validates digital product configuration
 * @param {Object} product - Product to validate
 * @returns {Object} Validation result with isValid and errors
 */
export const validateDigitalProduct = (product) => {
  const errors = [];

  if (!product.name) errors.push("Product name is required");
  if (!product.price || product.price <= 0)
    errors.push("Valid price is required");
  if (isDigitalProduct(product)) {
    if (
      !product.downloadable_files ||
      product.downloadable_files.length === 0
    ) {
      errors.push("Digital products must have downloadable files");
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};
